/**
 * Deal Intelligence Page
 * Comprehensive deal intelligence analysis for sellers
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { dealIntelligenceAPI, sellersAPI } from '../../services/api';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Minus,
  BarChart3,
  Building2,
  Lightbulb,
  Target,
  Clock,
  AlertCircle,
  Sparkles,
  ArrowLeft,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Flame,
  Zap,
  Filter,
  Search,
} from 'lucide-react';

export default function DealIntelligencePage() {
  const { user } = useAuth();
  const sellerId = user?.sellerId;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [comparableFilter, setComparableFilter] = useState('all');

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      if (!sellerId) {
        setLoading(false);
        setError('No seller profile found');
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await dealIntelligenceAPI.getIntelligence(sellerId);

        // Only update state if component is still mounted
        if (!isMounted) return;

        if (response.data?.success) {
          setData(response.data.data);
        } else {
          setError(response.data?.error || 'Failed to load intelligence data');
        }
      } catch (err) {
        // Only update state if component is still mounted
        if (!isMounted) return;
        console.error('[DealIntelligence] Error:', err);
        setError(err.response?.data?.error || err.message || 'Failed to fetch deal intelligence');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    // Cleanup function to prevent state updates on unmounted component
    return () => {
      isMounted = false;
    };
  }, [sellerId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await dealIntelligenceAPI.getIntelligence(sellerId);
      if (response.data?.success) {
        setData(response.data.data);
      } else {
        setError(response.data?.error || 'Failed to load intelligence data');
      }
    } catch (err) {
      console.error('[DealIntelligence] Error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to fetch deal intelligence');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      const response = await dealIntelligenceAPI.refresh(sellerId);
      if (response.data?.success) {
        setData(response.data.data);
      }
    } catch (err) {
      console.error('Failed to refresh:', err);
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-48 bg-gray-100 rounded-xl mb-6"></div>
          <div className="h-64 bg-gray-100 rounded-xl"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertCircle className="w-8 h-8 text-red-500" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Failed to Load Intelligence</h2>
        <p className="text-gray-500 mb-6">{error}</p>
        <div className="flex items-center justify-center gap-4">
          <button
            onClick={fetchData}
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700"
          >
            <RefreshCw className="w-4 h-4" />
            Retry
          </button>
          <Link
            to="/seller/dashboard"
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200"
          >
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <DollarSign className="w-8 h-8 text-gray-400" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">No Intelligence Available</h2>
        <p className="text-gray-500 mb-6">Complete your profile to get deal intelligence</p>
        <Link
          to="/seller/profile"
          className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700"
        >
          Complete Profile
        </Link>
      </div>
    );
  }

  const { valuation, comparableDeals, insights, recommendedBuyers, marketContext } = data;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'comparables', label: 'Comparable Deals', icon: DollarSign, count: comparableDeals?.length },
    { id: 'insights', label: 'Market Insights', icon: Lightbulb, count: insights?.length },
    { id: 'buyers', label: 'Active Acquirers', icon: Building2, count: recommendedBuyers?.length },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/seller/dashboard"
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Deal Intelligence</h1>
            <p className="text-gray-500">Your market valuation and comparable deals</p>
          </div>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh Data
        </button>
      </div>

      {/* Hero Valuation Card */}
      <ValuationHero valuation={valuation} marketContext={marketContext} />

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-6">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-3 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-emerald-500 text-emerald-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
                {tab.count > 0 && (
                  <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                    activeTab === tab.id ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {activeTab === 'overview' && (
          <OverviewTab
            valuation={valuation}
            insights={insights}
            comparableDeals={comparableDeals}
            recommendedBuyers={recommendedBuyers}
            marketContext={marketContext}
          />
        )}
        {activeTab === 'comparables' && (
          <ComparablesTab
            deals={comparableDeals}
            filter={comparableFilter}
            onFilterChange={setComparableFilter}
          />
        )}
        {activeTab === 'insights' && (
          <InsightsTab insights={insights} />
        )}
        {activeTab === 'buyers' && (
          <BuyersTab buyers={recommendedBuyers} />
        )}
      </div>
    </div>
  );
}

// Hero Valuation Card
function ValuationHero({ valuation, marketContext }) {
  if (!valuation || valuation.confidence === 'insufficient_data') {
    return (
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 border border-gray-200">
        <div className="flex items-center gap-4">
          <AlertCircle className="w-8 h-8 text-amber-500" />
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Limited Data Available</h2>
            <p className="text-gray-600">
              Add more details to your assets to receive accurate valuation estimates
            </p>
          </div>
        </div>
      </div>
    );
  }

  const { upfront, total, confidence, comparableCount, adjustments } = valuation;

  const confidenceConfig = {
    high: { color: 'text-green-600', bg: 'bg-green-100', label: 'High Confidence' },
    medium: { color: 'text-amber-600', bg: 'bg-amber-100', label: 'Medium Confidence' },
    low: { color: 'text-gray-600', bg: 'bg-gray-100', label: 'Low Confidence' },
  };
  const conf = confidenceConfig[confidence] || confidenceConfig.low;

  return (
    <div className="bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50 rounded-2xl p-8 border border-emerald-100">
      <div className="grid md:grid-cols-2 gap-8">
        {/* Left - Main Valuation */}
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-green-500 rounded-xl flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Estimated Upfront Value</h2>
              <span className={`text-xs px-2 py-0.5 rounded-full ${conf.bg} ${conf.color}`}>
                {conf.label}
              </span>
            </div>
          </div>

          {upfront && (
            <>
              <div className="flex items-baseline gap-3 mb-4">
                <span className="text-5xl font-bold text-gray-900">${upfront.mid}M</span>
                <span className="text-gray-500">median</span>
              </div>

              <div className="flex items-center gap-3 text-gray-600 mb-4">
                <span className="text-lg">${upfront.low}M</span>
                <div className="flex-1 h-3 bg-white rounded-full relative overflow-hidden border border-gray-200">
                  <div
                    className="absolute inset-y-0 bg-gradient-to-r from-emerald-400 to-green-500 rounded-full"
                    style={{ left: '20%', right: '20%' }}
                  />
                  <div
                    className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-emerald-600 rounded-full border-3 border-white shadow-lg"
                    style={{ left: '50%', marginLeft: '-8px' }}
                  />
                </div>
                <span className="text-lg">${upfront.high}M</span>
              </div>
            </>
          )}

          {total && (
            <div className="pt-4 border-t border-emerald-200">
              <p className="text-sm text-gray-600">
                Total Deal Value Range:{' '}
                <span className="font-semibold text-gray-900">
                  ${total.low}M - ${total.high}M
                </span>
              </p>
            </div>
          )}
        </div>

        {/* Right - Context */}
        <div className="space-y-4">
          {/* Market Trend */}
          {marketContext && (
            <div className="bg-white rounded-xl p-4 border border-gray-100">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Market Trend</h3>
              <div className="flex items-center gap-3">
                <TrendIndicatorLarge trend={marketContext.trend} trendPct={marketContext.trendPct} />
                <div>
                  <p className="text-sm text-gray-600">
                    {marketContext.recentDealsCount} deals in the last 12 months
                  </p>
                  <p className="text-xs text-gray-400">
                    Activity level: {marketContext.activityLevel}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Adjustments */}
          {adjustments && (
            <div className="bg-white rounded-xl p-4 border border-gray-100">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Valuation Adjustments</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Stage Adjustment</span>
                  <span className={`font-medium ${parseFloat(adjustments.stageMultiplier) >= 1 ? 'text-green-600' : 'text-red-600'}`}>
                    {parseFloat(adjustments.stageMultiplier) >= 1 ? '+' : ''}{((parseFloat(adjustments.stageMultiplier) - 1) * 100).toFixed(0)}%
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Modality Premium</span>
                  <span className={`font-medium ${parseFloat(adjustments.modalityPremium) >= 1 ? 'text-green-600' : 'text-red-600'}`}>
                    {parseFloat(adjustments.modalityPremium) >= 1 ? '+' : ''}{((parseFloat(adjustments.modalityPremium) - 1) * 100).toFixed(0)}%
                  </span>
                </div>
              </div>
            </div>
          )}

          <p className="text-xs text-gray-400 text-center">
            Based on {comparableCount} comparable deals
          </p>
        </div>
      </div>
    </div>
  );
}

// Overview Tab
function OverviewTab({ valuation, insights, comparableDeals, recommendedBuyers, marketContext }) {
  return (
    <div className="grid md:grid-cols-2 gap-6">
      {/* Top Insights */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Lightbulb className="w-5 h-5 text-amber-500" />
          Key Insights
        </h3>
        <div className="space-y-3">
          {insights?.slice(0, 3).map((insight, index) => (
            <InsightCard key={index} insight={insight} compact />
          ))}
          {(!insights || insights.length === 0) && (
            <p className="text-gray-500 text-sm">No insights available yet</p>
          )}
        </div>
      </div>

      {/* Recent Deals */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-emerald-500" />
          Top Comparable Deals
        </h3>
        <div className="space-y-3">
          {comparableDeals?.slice(0, 3).map((deal, index) => (
            <DealCard key={deal.id || index} deal={deal} compact />
          ))}
          {(!comparableDeals || comparableDeals.length === 0) && (
            <p className="text-gray-500 text-sm">No comparable deals found</p>
          )}
        </div>
      </div>

      {/* Active Acquirers */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 md:col-span-2">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Building2 className="w-5 h-5 text-indigo-500" />
          Most Active Acquirers in Your Space
        </h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {recommendedBuyers?.slice(0, 6).map((buyer, index) => (
            <BuyerCard key={buyer.buyerId || index} buyer={buyer} />
          ))}
          {(!recommendedBuyers || recommendedBuyers.length === 0) && (
            <p className="text-gray-500 text-sm col-span-full">No buyer recommendations available</p>
          )}
        </div>
      </div>
    </div>
  );
}

// Comparables Tab
function ComparablesTab({ deals, filter, onFilterChange }) {
  const filteredDeals = deals?.filter(deal => {
    if (filter === 'all') return true;
    if (filter === 'high') return deal.similarityScore >= 70;
    if (filter === 'mna') return deal.deal_type?.toLowerCase().includes('acquisition') || deal.deal_type?.toLowerCase().includes('m&a');
    if (filter === 'licensing') return deal.deal_type?.toLowerCase().includes('licen');
    return true;
  }) || [];

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center gap-3">
        <Filter className="w-4 h-4 text-gray-400" />
        <div className="flex gap-2">
          {[
            { id: 'all', label: 'All Deals' },
            { id: 'high', label: 'High Match' },
            { id: 'mna', label: 'M&A' },
            { id: 'licensing', label: 'Licensing' },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => onFilterChange(f.id)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filter === f.id
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Deals Grid */}
      <div className="grid gap-4">
        {filteredDeals.map((deal, index) => (
          <DealCard key={deal.id || index} deal={deal} />
        ))}
        {filteredDeals.length === 0 && (
          <div className="text-center py-12">
            <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No deals match your filter</p>
          </div>
        )}
      </div>
    </div>
  );
}

// Insights Tab
function InsightsTab({ insights }) {
  const groupedInsights = {
    market_timing: insights?.filter(i => i.type === 'market_timing') || [],
    emphasis: insights?.filter(i => i.type === 'emphasis') || [],
    buyer_fit: insights?.filter(i => i.type === 'buyer_fit') || [],
    valuation_driver: insights?.filter(i => i.type === 'valuation_driver') || [],
    risk_factor: insights?.filter(i => i.type === 'risk_factor') || [],
    opportunity: insights?.filter(i => i.type === 'opportunity') || [],
  };

  const categories = [
    { key: 'market_timing', label: 'Market Timing', icon: Clock, color: 'blue' },
    { key: 'emphasis', label: 'What to Emphasize', icon: Target, color: 'purple' },
    { key: 'buyer_fit', label: 'Buyer Fit', icon: Building2, color: 'indigo' },
    { key: 'valuation_driver', label: 'Valuation Drivers', icon: TrendingUp, color: 'green' },
    { key: 'risk_factor', label: 'Risk Factors', icon: AlertCircle, color: 'amber' },
    { key: 'opportunity', label: 'Opportunities', icon: Sparkles, color: 'emerald' },
  ];

  return (
    <div className="space-y-6">
      {categories.map((cat) => {
        const catInsights = groupedInsights[cat.key];
        if (catInsights.length === 0) return null;

        const Icon = cat.icon;
        const colorClasses = {
          blue: 'text-blue-600 bg-blue-100',
          purple: 'text-purple-600 bg-purple-100',
          indigo: 'text-indigo-600 bg-indigo-100',
          green: 'text-green-600 bg-green-100',
          amber: 'text-amber-600 bg-amber-100',
          emerald: 'text-emerald-600 bg-emerald-100',
        };

        return (
          <div key={cat.key}>
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <div className={`w-6 h-6 rounded-lg flex items-center justify-center ${colorClasses[cat.color]}`}>
                <Icon className="w-4 h-4" />
              </div>
              {cat.label}
            </h3>
            <div className="grid gap-3">
              {catInsights.map((insight, index) => (
                <InsightCard key={index} insight={insight} />
              ))}
            </div>
          </div>
        );
      })}
      {(!insights || insights.length === 0) && (
        <div className="text-center py-12">
          <Lightbulb className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No insights available yet</p>
        </div>
      )}
    </div>
  );
}

// Buyers Tab
function BuyersTab({ buyers }) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {buyers?.map((buyer, index) => (
        <BuyerCard key={buyer.buyerId || index} buyer={buyer} detailed />
      ))}
      {(!buyers || buyers.length === 0) && (
        <div className="col-span-full text-center py-12">
          <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No buyer recommendations available</p>
        </div>
      )}
    </div>
  );
}

// Component: Trend Indicator Large
function TrendIndicatorLarge({ trend, trendPct }) {
  const config = {
    rising: { icon: TrendingUp, color: 'text-green-600', bg: 'bg-green-100', label: 'Rising' },
    declining: { icon: TrendingDown, color: 'text-red-600', bg: 'bg-red-100', label: 'Declining' },
    stable: { icon: Minus, color: 'text-gray-600', bg: 'bg-gray-100', label: 'Stable' },
  };
  const { icon: Icon, color, bg, label } = config[trend] || config.stable;

  return (
    <div className={`w-12 h-12 ${bg} rounded-xl flex items-center justify-center`}>
      <Icon className={`w-6 h-6 ${color}`} />
    </div>
  );
}

// Component: Insight Card
function InsightCard({ insight, compact = false }) {
  const typeConfig = {
    market_timing: { icon: Clock, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-100' },
    emphasis: { icon: Target, color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-100' },
    buyer_fit: { icon: Building2, color: 'text-indigo-600', bg: 'bg-indigo-50', border: 'border-indigo-100' },
    valuation_driver: { icon: TrendingUp, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-100' },
    risk_factor: { icon: AlertCircle, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-100' },
    opportunity: { icon: Sparkles, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-100' },
  };
  const config = typeConfig[insight.type] || typeConfig.opportunity;
  const Icon = config.icon;

  return (
    <div className={`p-4 ${config.bg} rounded-xl border ${config.border}`}>
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 ${config.color} flex-shrink-0 mt-0.5`} />
        <div className="flex-1">
          <p className={`font-semibold ${config.color} ${compact ? 'text-sm' : ''}`}>
            {insight.title}
          </p>
          <p className={`text-gray-700 mt-1 ${compact ? 'text-sm' : ''}`}>
            {insight.description}
          </p>
          {insight.recommendation && !compact && (
            <div className="mt-3 flex items-start gap-2 text-sm text-gray-600">
              <Lightbulb className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <span>{insight.recommendation}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Component: Deal Card
function DealCard({ deal, compact = false }) {
  return (
    <div className={`bg-white rounded-xl border border-gray-200 ${compact ? 'p-3' : 'p-4'}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <p className={`font-semibold text-gray-900 ${compact ? 'text-sm' : ''}`}>
            {deal.headline || `${deal.buyer} → ${deal.seller}`}
          </p>
          <div className={`flex flex-wrap items-center gap-2 mt-1 ${compact ? 'text-xs' : 'text-sm'} text-gray-500`}>
            {deal.therapeutic_area && (
              <span className="inline-flex items-center gap-1">
                <Target className="w-3 h-3" />
                {deal.therapeutic_area}
              </span>
            )}
            {deal.modality && (
              <span className="inline-flex items-center gap-1">
                <Zap className="w-3 h-3" />
                {deal.modality}
              </span>
            )}
            {deal.stage && <span>• {deal.stage}</span>}
            {deal.deal_date && (
              <span>• {new Date(deal.deal_date).getFullYear()}</span>
            )}
          </div>
          {!compact && deal.deal_summary && (
            <p className="text-sm text-gray-600 mt-2 line-clamp-2">
              {deal.deal_summary}
            </p>
          )}
        </div>
        <div className="text-right flex-shrink-0">
          {deal.upfront ? (
            <p className={`font-bold text-gray-900 ${compact ? 'text-lg' : 'text-xl'}`}>
              ${formatValue(deal.upfront)}
            </p>
          ) : deal.total ? (
            <p className={`font-bold text-gray-900 ${compact ? 'text-lg' : 'text-xl'}`}>
              ${formatValue(deal.total)}
            </p>
          ) : null}
          <p className={`text-emerald-600 font-medium ${compact ? 'text-xs' : 'text-sm'}`}>
            {deal.similarityScore}% match
          </p>
        </div>
      </div>
    </div>
  );
}

// Component: Buyer Card
function BuyerCard({ buyer, detailed = false }) {
  const urgencyColors = {
    high: 'bg-green-100 text-green-700',
    medium: 'bg-amber-100 text-amber-700',
    low: 'bg-gray-100 text-gray-600',
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center flex-shrink-0">
          <Building2 className="w-6 h-6 text-indigo-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className="font-semibold text-gray-900 truncate">{buyer.companyName}</p>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${urgencyColors[buyer.urgencyLevel]}`}>
              {buyer.matchScore}% fit
            </span>
          </div>
          <p className="text-sm text-gray-500 mt-1">{buyer.dealHistory}</p>
          {detailed && buyer.relevantDeals?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <p className="text-xs font-medium text-gray-500 mb-2">Recent Relevant Deals:</p>
              {buyer.relevantDeals.slice(0, 2).map((deal, index) => (
                <p key={index} className="text-xs text-gray-600 truncate">
                  • {deal.headline}
                </p>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Helper function
function formatValue(valueInMillions) {
  if (valueInMillions >= 1000) {
    return `${(valueInMillions / 1000).toFixed(1)}B`;
  }
  return `${Math.round(valueInMillions)}M`;
}
